import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Conversation from './Conversation';
import ConversationSkeleton from './ConversationSkeleton';
import InboxEmptyState from '../InboxEmptyState/InboxEmptyState';
import PitchRow from '../PitchRow';
import PitchUserActionsDropdown from '../../my_requests/pitch_inbox/PitchUserActionsDropdown';

// IntersectionObserver root: the inbox's own scroll container, not the page viewport.
function findScrollParent(node) {
  let parent = node?.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

function ConversationsList({
  conversations,
  selectedConversation,
  setConversation,
  onConversationActionComplete,
  selectionMode = false,
  selectedConversationIds = [],
  onToggleConversationSelection,
  searchQuery = null,
  isSearching = false,
  hasActiveFilters = false,
  onClearFilters = null,
  activeMailbox = null,
  onLoadMore = null,
  hasMore = false,
  isLoadingMore = false,
  isLoading = false,
  isMedia = false,
  onSelectPitchRow = null,
  selectedPitchId = null,
  // Lifted mute/report state (V1's MyInbox supplies it; null here until V2 grows a mute
  // model) — used to compute the pitch rows' Muted/Reported badges.
  muteReport = null,
}) {
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const userScrolledRef = useRef(false);

  // Auto-scroll on selection change OR on list update IF the user hasn't manually scrolled
  // since — handles deep-link inject (including pages loading in around a deep selection)
  // without yanking on realtime. Manual scrolling is detected from real input gestures,
  // NOT scrollTop deltas: browser scroll anchoring shifts scrollTop whenever rows load
  // in above the viewport, which made delta-comparison false-positive and strand deep
  // selections out of view. Virtual pitch rows carry data-conversation-id="pitch-<id>",
  // so a selected pitch anchors the viewport the same way as a conversation.
  const selectedId = selectedConversation?.conversation_id ?? (selectedPitchId != null ? `pitch-${selectedPitchId}` : null);

  // (Re)bind on every list render, not just mount: at mount the component may be showing
  // the empty/loading state, so listRef (and thus the scroll parent) doesn't exist yet —
  // a mount-only bind would attach nothing and every page-load would re-scroll on the user.
  const gestureParentRef = useRef(null);
  const markUserScrollRef = useRef(() => {
    userScrolledRef.current = true;
  });
  useEffect(() => {
    const parent = listRef.current && findScrollParent(listRef.current);
    if (!parent || parent === gestureParentRef.current) return;
    const handler = markUserScrollRef.current;
    const previous = gestureParentRef.current;
    if (previous) {
      previous.removeEventListener('wheel', handler);
      previous.removeEventListener('touchmove', handler);
      previous.removeEventListener('pointerdown', handler);
      previous.removeEventListener('keydown', handler);
    }
    parent.addEventListener('wheel', handler, { passive: true });
    parent.addEventListener('touchmove', handler, { passive: true });
    parent.addEventListener('pointerdown', handler, { passive: true });
    // Keyboard scrolling counts as a gesture too (PageDown/arrows/space over the list).
    parent.addEventListener('keydown', handler, { passive: true });
    gestureParentRef.current = parent;
  }, [conversations]);
  useEffect(
    () => () => {
      const parent = gestureParentRef.current;
      const handler = markUserScrollRef.current;
      if (parent) {
        parent.removeEventListener('wheel', handler);
        parent.removeEventListener('touchmove', handler);
        parent.removeEventListener('pointerdown', handler);
        parent.removeEventListener('keydown', handler);
      }
    },
    [],
  );

  // A tab switch rebuilds the list — a selection carried into the new tab scrolls back
  // into view even if the user had scrolled the previous tab.
  useEffect(() => {
    userScrolledRef.current = false;
  }, [activeMailbox]);

  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    const isNewSelection = lastSelectedIdRef.current !== selectedId;
    if (isNewSelection) userScrolledRef.current = false;
    if (!isNewSelection && userScrolledRef.current) return;

    const row = listRef.current.querySelector(`[data-conversation-id="${selectedId}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    lastSelectedIdRef.current = selectedId;
  }, [selectedId, conversations]);

  // `conversations` dep re-observes after each list update so IntersectionObserver delivers
  // a fresh initial callback — otherwise it wouldn't re-fire if the sentinel stays in view.
  useEffect(() => {
    if (!onLoadMore || !hasMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const root = findScrollParent(sentinel);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { root, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, conversations]);

  const handleConversationClick = (conversationId) => {
    setConversation(conversations.find((convo) => convo.conversation_id === conversationId));
  };

  // Search in flight (debounce + fetch): skeleton rows, same flash-guard as isLoading below —
  // otherwise "Your Inbox is empty" flashes until the results arrive.
  if (conversations.length === 0 && searchQuery && isSearching) return <ConversationSkeleton />;

  if (conversations.length === 0 && searchQuery && !isSearching) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center px-4 pt-5">
        <p className="paragraph-lg fw-semibold text-body mb-1">No results for &ldquo;{searchQuery}&rdquo;</p>
        <p className="paragraph-md text-secondary mb-0">Search looks through message content and pitches</p>
      </div>
    );
  }

  // Distinct from an empty inbox — offer Clear filters.
  if (conversations.length === 0 && hasActiveFilters) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center px-4 pt-5">
        <p className="paragraph-lg fw-semibold text-body mb-1">No conversations match your filters</p>
        <p className="paragraph-md text-secondary mb-3">Try a different filter, or clear them to see everything.</p>
        {onClearFilters && (
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </div>
    );
  }

  // Truly empty (no search, no filters): inbox / trash empty-state with CTAs.
  // Show skeleton rows during in-flight fetch — otherwise the empty state flashes before
  // the first response arrives (initial mount or mailbox/filter switch).
  if (conversations.length === 0 && isLoading) return <ConversationSkeleton />;
  if (conversations.length === 0) {
    return <InboxEmptyState mailbox={activeMailbox} isMedia={isMedia} />;
  }

  return (
    <>
      <ul ref={listRef} className="list-group rounded-0 overflow-x-hidden">
        {conversations.map((convo) => (convo.feed_row_type === 'pitch' ? (
            <PitchRow
              key={convo.conversation_id}
              pitch={convo.pitch}
              muted={
                muteReport
                  ? !!(
                      convo.pitch.user_migrate_id
                      && (muteReport.mutedUserIds || []).includes(convo.pitch.user_migrate_id)
                    )
                  : null
              }
              reported={
                muteReport
                  ? !!(convo.pitch.migrate_id && (muteReport.reportedPitchIds || []).includes(convo.pitch.migrate_id))
                  : null
              }
              selected={convo.pitch.id === selectedPitchId}
              onSelect={() => onSelectPitchRow?.(convo.pitch)}
              selectionMode={selectionMode}
              isSelected={selectedConversationIds.includes(convo.conversation_id)}
              onToggleSelection={onToggleConversationSelection}
              actions={
                // Same visibility rule as Conversation rows: menu replaces the timestamp
                // only on the selected row, and never during bulk-manage mode.
                !selectionMode && convo.pitch.id === selectedPitchId ? (
                  <PitchUserActionsDropdown pitch={convo.pitch} small />
                ) : null
              }
            />
          ) : (
            <Conversation
              conversation={convo}
              handleClick={handleConversationClick}
              key={convo.conversation_id}
              selectedConversation={selectedConversation}
              onActionComplete={onConversationActionComplete}
              selectionMode={selectionMode}
              isSelected={selectedConversationIds.includes(convo.conversation_id)}
              onToggleSelection={onToggleConversationSelection}
              activeMailbox={activeMailbox}
              inSearchResults={Boolean(searchQuery)}
            />
          )))}
      </ul>
      {hasMore && (
        <div ref={sentinelRef} className="d-flex justify-content-center py-3" aria-hidden="true">
          {isLoadingMore && (
            <div className="spinner-border spinner-border-sm text-secondary" role="status">
              <span className="visually-hidden">Loading more conversations…</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

ConversationsList.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      conversation_id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedConversation: PropTypes.shape({
    conversation_id: PropTypes.string,
  }),
  setConversation: PropTypes.func.isRequired,
  onConversationActionComplete: PropTypes.func.isRequired,
  selectionMode: PropTypes.bool,
  selectedConversationIds: PropTypes.arrayOf(PropTypes.string),
  onToggleConversationSelection: PropTypes.func,
  searchQuery: PropTypes.string,
  isSearching: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
  onClearFilters: PropTypes.func,
  activeMailbox: PropTypes.string,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMedia: PropTypes.bool,
  onSelectPitchRow: PropTypes.func,
  selectedPitchId: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types -- request-page-shaped config passed through
  muteReport: PropTypes.object,
};

export default ConversationsList;
